import React, { useCallback, useEffect, useRef, useState } from 'react';
import Form from 'react-bootstrap/Form';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Table from 'react-bootstrap/Table';
import axios from '../axios';
import NavigationBar from '../NavigationBar';
import { renderPagination, renderAdvancedPagination } from '../utilities';
import { notify, errorMessage } from '../notify';

// Shared state for the Manage Data list pages: page, search (applied after a short pause),
// and a guard so a slow, older reply never overwrites a newer one.
export function useManageTable(endpoint) {
    const [data, setData] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalRows, setTotalRows] = useState(0);
    const [search, setSearchValue] = useState('');
    const [appliedSearch, setAppliedSearch] = useState('');
    const [loading, setLoading] = useState(true);
    const [reloadKey, setReloadKey] = useState(0);
    const requestId = useRef(0);

    useEffect(() => {
        const timer = setTimeout(() => {
            setAppliedSearch(search.trim());
        }, 300);
        return () => clearTimeout(timer);
    }, [search]);

    useEffect(() => {
        const id = ++requestId.current;
        setLoading(true);
        axios.get(`${window.backendUrl}/${endpoint}`, { params: { page: currentPage, search: appliedSearch } })
            .then((response) => {
                if (id !== requestId.current) return;
                setData(response.data.data || []);
                setTotalPages(response.data.totalPages || 1);
                setTotalRows(response.data.totalRows ?? (response.data.data || []).length);
                // Deleting the last row of the last page leaves an empty page: step back.
                if (currentPage > 1 && response.data.totalPages && currentPage > response.data.totalPages) {
                    setCurrentPage(response.data.totalPages);
                }
            })
            .catch((error) => {
                if (id !== requestId.current) return;
                console.error('Error fetching data:', error);
                notify.error(errorMessage(error, 'Could not load the list.'));
            })
            .finally(() => {
                if (id === requestId.current) setLoading(false);
            });
    }, [endpoint, currentPage, appliedSearch, reloadKey]);

    const setSearch = useCallback((value) => {
        setSearchValue(value);
        setCurrentPage(1);
    }, []);

    const reload = useCallback(() => setReloadKey((k) => k + 1), []);

    return { data, currentPage, setCurrentPage, totalPages, totalRows, search, setSearch, loading, reload };
}

export function ManageSearch({ value, onChange, placeholder = 'Search...' }) {
    return (
        <div className="ft-search">
            <i className="fa fa-search" aria-hidden="true"></i>
            <Form.Control
                type="text"
                placeholder={placeholder}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Escape') onChange(''); }}
                aria-label={placeholder}
            />
            {value && (
                <button type="button" className="ft-search-clear" onClick={() => onChange('')} aria-label="Clear search">&times;</button>
            )}
        </div>
    );
}

export function TableMeta({ totalRows, search, loading }) {
    if (loading) return <span className="ft-table-meta"><i className="fa fa-circle-o-notch fa-spin" aria-hidden="true"></i> Loading…</span>;
    return (
        <span className="ft-table-meta">
            {totalRows} {totalRows === 1 ? 'record' : 'records'}{search ? ` matching “${search}”` : ''}
        </span>
    );
}

// Runs a save/update/delete request, shows a toast either way, and resolves true on success.
export async function runAction(request, successMessage, failMessage) {
    try {
        await request();
        notify.success(successMessage);
        return true;
    } catch (error) {
        console.error(failMessage, error);
        notify.error(errorMessage(error, failMessage));
        return false;
    }
}

// Table body for the Manage Data lists: stable keys, clickable rows (to edit), selected-row highlight,
// and a clear empty / no-match message.
export function ManageRows({ currentPage, data, fields, onRowClick, selected, idKey, search, loading }) {
    if (!data.length) {
        return (
            <tr className="ft-empty-row">
                <td colSpan={fields.length + 1}>
                    {loading ? (
                        <><i className="fa fa-circle-o-notch fa-spin" aria-hidden="true"></i>Loading…</>
                    ) : (
                        <><i className="fa fa-inbox" aria-hidden="true"></i>{search ? `No records match “${search}”.` : 'No records yet.'}</>
                    )}
                </td>
            </tr>
        );
    }
    return data.map((entry, index) => {
        const isSelected = selected && idKey && selected[idKey] === entry[idKey];
        const className = [onRowClick ? 'ft-clickable-row' : '', isSelected ? 'ft-selected-row' : ''].join(' ').trim() || undefined;
        return (
            <tr key={idKey && entry[idKey] != null ? entry[idKey] : index} className={className}
                onClick={onRowClick ? () => onRowClick(entry) : undefined}
                title={onRowClick ? 'Click to edit' : undefined}>
                <td>{(currentPage - 1) * 20 + index + 1}</td>
                {fields.map((field) => (
                    <td key={field}>{entry[field]}</td>
                ))}
            </tr>
        );
    });
}

// The standard Manage Data page: list with search and pagination on the left, the add/edit form on the right.
export function ManageDataPage({ title, endpoint, columns, idKey, FormComponent, searchPlaceholder, editable = true }) {
    const table = useManageTable(endpoint);
    const [selectedEntry, setSelectedEntry] = useState(null);

    const fields = columns.map((c) => c.field);
    return (
        <>
            <NavigationBar />
            <Container fluid>
                <Row style={{ marginTop: '20px' }}>
                    <Col xs={2}></Col>
                    <Col xs={7}>
                        <Row className='d-flex justify-space-between mb-3 align-items-center'>
                            <Col>
                                <h3 style={{ margin: '0px', textAlign: 'left' }}>{title}</h3>
                            </Col>
                            <Col className='d-flex justify-content-end'>
                                {renderPagination({ totalPages: table.totalPages, currentPage: table.currentPage, setCurrentPage: table.setCurrentPage })}
                                {renderAdvancedPagination({ totalPages: table.totalPages, currentPage: table.currentPage, setCurrentPage: table.setCurrentPage })}
                            </Col>
                        </Row>
                        <Row>
                            <Col>
                                <div className="d-flex justify-content-between align-items-center mb-2">
                                    <TableMeta totalRows={table.totalRows} search={table.search.trim()} loading={table.loading} />
                                    <ManageSearch value={table.search} onChange={table.setSearch} placeholder={searchPlaceholder} />
                                </div>
                                <Table striped bordered hover className={table.loading && table.data.length ? 'ft-loading' : undefined}>
                                    <thead>
                                        <tr>
                                            <th style={{ width: '60px' }}>SL</th>
                                            {columns.map((c) => <th key={c.field} style={c.width ? { width: c.width } : undefined}>{c.label}</th>)}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <ManageRows
                                            currentPage={table.currentPage}
                                            data={table.data}
                                            fields={fields}
                                            idKey={idKey}
                                            selected={selectedEntry}
                                            onRowClick={editable ? setSelectedEntry : undefined}
                                            search={table.search.trim()}
                                            loading={table.loading}
                                        />
                                    </tbody>
                                </Table>
                            </Col>
                        </Row>
                    </Col>
                    <Col>
                        <FormComponent onChanged={table.reload} selectedEntry={selectedEntry} setSelectedEntry={setSelectedEntry} />
                    </Col>
                </Row>
            </Container>
        </>
    );
}
